<template>
  <section class="features-section section" id="features">
    <div class="container">
      <div class="section-header">
        <div class="section-tag">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
          产品功能
        </div>
        <h2 class="section-title">点石引擎 <span class="gradient-text">产品功能</span></h2>
        <p class="section-subtitle">
          探索工作流商店，集结全能智能体。专为办公场景打造，<br>让每一个任务都有AI助阵。
        </p>
      </div>

      <!-- Marquee Row 1 -->
      <div class="marquee-container" style="margin-bottom: 16px;">
        <div class="marquee-track">
          <div class="marquee-item" v-for="f in marqueeRow1" :key="f.id">
            <div class="marquee-icon" :style="{ background: f.color }">
              <svg class="marquee-icon-svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" preserveAspectRatio="xMidYMid meet"><path :d="f.icon"/></svg>
            </div>
            <span>{{ f.name }}</span>
          </div>
        </div>
      </div>

      <!-- Marquee Row 2 (reverse) -->
      <div class="marquee-container marquee-reverse" style="margin-bottom: 56px;">
        <div class="marquee-track">
          <div class="marquee-item" v-for="f in marqueeRow2" :key="f.id">
            <div class="marquee-icon" :style="{ background: f.color }">
              <svg class="marquee-icon-svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" preserveAspectRatio="xMidYMid meet"><path :d="f.icon"/></svg>
            </div>
            <span>{{ f.name }}</span>
          </div>
        </div>
      </div>

      <!-- Row 1: 2 Hero cards (1×2) -->
      <div class="features-hero-row">
        <div
          v-for="(feature, i) in heroFeatures"
          :key="'h'+i"
          class="feature-card hero-card"
          :class="{ active: activeFeature === 'h'+i }"
          @mouseenter="activeFeature = 'h'+i"
          @mouseleave="activeFeature = null"
        >
          <div class="feature-top">
            <div class="feature-icon large-icon" :style="{ background: feature.color }">
              <svg class="feature-icon-svg feature-icon-svg--lg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" preserveAspectRatio="xMidYMid meet"><path :d="feature.icon"/></svg>
            </div>
            <div class="feature-label" v-if="feature.label">{{ feature.label }}</div>
          </div>
          <h3 class="feature-name hero-name">{{ feature.name }}</h3>
          <p class="feature-desc">{{ feature.desc }}</p>

          <!-- Stat badge -->
          <div class="hero-stat" :style="{ background: feature.color }">
            <span class="stat-val">{{ feature.stat.val }}</span>
            <span class="stat-label">{{ feature.stat.label }}</span>
          </div>

          <!-- Visual -->
          <div class="feature-visual">
            <div v-if="feature.visual === 'agents'" class="mini-agents">
              <div v-for="(a, j) in agentAvatars" :key="j" class="mini-agent" :style="{ background: a, animationDelay: j*0.1+'s' }">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><circle cx="12" cy="7" r="4"/><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/></svg>
              </div>
            </div>
            <div v-if="feature.visual === 'flow'" class="mini-flow-grid">
              <div v-for="n in 6" :key="n" class="flow-grid-node" :style="{ background: feature.color, animationDelay: n*0.12+'s' }">
                <svg class="flow-node-icon" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" preserveAspectRatio="xMidYMid meet"><path d="M5 5H11V11H5zM13 5H19V11H13zM5 13H11V19H5zM13 13H19V19H13z"/></svg>
              </div>
            </div>
          </div>

          <!-- Tags -->
          <div class="feature-tags">
            <span v-for="tag in feature.tags" :key="tag" class="feature-tag">{{ tag }}</span>
          </div>

          <router-link :to="feature.ctaTo" class="hero-cta-btn">
            立即使用
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </router-link>

          <div class="feature-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </div>
      </div>

      <!-- Row 2+3: 8 small cards (4×2) -->
      <div class="features-small-grid">
        <router-link
          v-for="(feature, i) in features"
          :key="'s'+i"
          tag="div"
          :to="feature.ctaTo"
          class="feature-card small-card"
          :class="{ active: activeFeature === 's'+i }"
          @mouseenter.native="activeFeature = 's'+i"
          @mouseleave.native="activeFeature = null"
        >
          <div class="feature-top">
            <div class="feature-icon" :style="{ background: feature.color }">
              <svg class="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" preserveAspectRatio="xMidYMid meet"><path :d="feature.icon"/></svg>
            </div>
            <div class="feature-label" v-if="feature.label">{{ feature.label }}</div>
          </div>
          <h3 class="feature-name">{{ feature.name }}</h3>
          <p class="feature-desc small-desc">{{ feature.desc }}</p>

          <div class="feature-visual small-visual" v-if="feature.visual">
            <div v-if="feature.visual === 'chart'" class="mini-chart">
              <div v-for="(h, j) in [40,65,50,80,60,90,70,85]" :key="j" class="chart-bar" :style="{ height: h+'%', animationDelay: j*0.08+'s' }"></div>
            </div>
            <div v-if="feature.visual === 'video'" class="mini-video">
              <div v-for="n in 3" :key="n" class="video-frame" :style="{ background: videoColors[n-1], animationDelay: n*0.15+'s' }">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              </div>
            </div>
            <div v-if="feature.visual === 'music'" class="music-waves small-waves">
              <div v-for="n in 8" :key="n" class="wave-bar" :style="{ animationDelay: n*0.07+'s', height: (12 + Math.abs(n-4)*5)+'px' }"></div>
            </div>
            <div v-if="feature.visual === 'text'" class="text-lines small-text">
              <div v-for="n in 3" :key="n" class="text-line" :style="{ width: (90 - n*8)+'%', animationDelay: n*0.1+'s' }"></div>
            </div>
          </div>

          <div class="feature-arrow">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import {
  AGENTS_PLAZA,
  WORKFLOW_STORE,
  AGENTS_WRITING,
  AGENTS_ASSISTANT,
  AGENTS_APPS,
} from '@/constants/homeNav'

const baseFeatures = [
  { id: 'f1', name: 'AI智能体', icon: 'M12 2a10 10 0 110 20 10 10 0 010-20z', color: 'linear-gradient(135deg,#2563EB,#06B6D4)' },
  { id: 'f2', name: 'AI营销', icon: 'M22 7L13.5 15.5 8.5 10.5 2 17', color: 'linear-gradient(135deg,#7C3AED,#EC4899)' },
  { id: 'f3', name: 'AI招商助理', icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8z', color: 'linear-gradient(135deg,#0EA5E9,#10B981)' },
  { id: 'f4', name: 'AI财务', icon: 'M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6', color: 'linear-gradient(135deg,#F59E0B,#EF4444)' },
  { id: 'f5', name: 'AI客服', icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z', color: 'linear-gradient(135deg,#10B981,#059669)' },
  { id: 'f6', name: 'AI文案', icon: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7', color: 'linear-gradient(135deg,#6366F1,#8B5CF6)' },
  { id: 'f7', name: 'AI视频', icon: 'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zM10 9.5L15.5 12 10 14.5z', color: 'linear-gradient(135deg,#EC4899,#F43F5E)' },
  { id: 'f8', name: 'AI音乐', icon: 'M9 18V5l12-2v13M9 9l12-2', color: 'linear-gradient(135deg,#14B8A6,#06B6D4)' },
  { id: 'f9', name: 'AI招商', icon: 'M18 8h1a4 4 0 010 8h-1M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8zM6 1v3M10 1v3M14 1v3', color: 'linear-gradient(135deg,#F97316,#EAB308)' },
  { id: 'f10', name: '短视频矩阵', icon: 'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zM10 9.5L15.5 12 10 14.5z', color: 'linear-gradient(135deg,#7C3AED,#2563EB)' },
  { id: 'f11', name: 'AI检索', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z', color: 'linear-gradient(135deg,#0EA5E9,#6366F1)' },
  { id: 'f12', name: 'AI数据分析', icon: 'M3 3v18h18M7 16l4-4 4 4 4-4', color: 'linear-gradient(135deg,#10B981,#14B8A6)' },
]

export default {
  name: 'FeaturesSection',
  data() {
    return {
      activeFeature: null,
      agentAvatars: [
        'linear-gradient(135deg,#2563EB,#06B6D4)',
        'linear-gradient(135deg,#7C3AED,#EC4899)',
        'linear-gradient(135deg,#0EA5E9,#10B981)',
        'linear-gradient(135deg,#F59E0B,#EF4444)',
        'linear-gradient(135deg,#EC4899,#F43F5E)',
        'linear-gradient(135deg,#10B981,#059669)',
      ],
      videoColors: [
        'linear-gradient(135deg,#EC4899,#F43F5E)',
        'linear-gradient(135deg,#7C3AED,#2563EB)',
        'linear-gradient(135deg,#0EA5E9,#06B6D4)',
      ],
      /* 第一排：2张大卡片 */
      heroFeatures: [
        {
          name: 'AI智能体中心',
          desc: '500+行业专属AI智能体，覆盖文案、营销、财务、客服等全场景，开箱即用，24小时不间断工作。随时召唤你的数字员工团队。',
          icon: 'M12 2a10 10 0 110 20 10 10 0 010-20z',
          color: 'linear-gradient(135deg,#2563EB,#06B6D4)',
          label: '核心功能',
          visual: 'agents',
          tags: ['文案AI', '营销AI', '客服AI', '财务AI', '招商AI'],
          stat: { val: '500+', label: '智能体' },
          ctaTo: AGENTS_PLAZA,
        },
        {
          name: 'AI工作流商店',
          desc: '1000+精选工作流模板，告别繁琐搭建，直接套用，开箱即用，秒变AI高手。从市场调研到视频脚本，让成功经验可复制。',
          icon: 'M5 5H11V11H5zM13 5H19V11H13zM5 13H11V19H5zM13 13H19V19H13z',
          color: 'linear-gradient(135deg,#7C3AED,#EC4899)',
          label: '精选推荐',
          visual: 'flow',
          tags: ['视频脚本', '营销方案', '数据报告', '电商运营'],
          stat: { val: '1000+', label: '工作流' },
          ctaTo: WORKFLOW_STORE,
        },
      ],
      /* 第二排+第三排：8张小卡片 */
      features: [
        {
          name: 'AI短视频',
          desc: '批量生成创意短视频，支持口播、宣传片、数字人等多种形式',
          icon: 'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zM10 9.5L15.5 12 10 14.5z',
          color: 'linear-gradient(135deg,#EC4899,#F43F5E)',
          label: '热门',
          visual: 'video',
          ctaTo: AGENTS_ASSISTANT,
        },
        {
          name: 'AI文案创作',
          desc: '覆盖小红书、抖音、微信等平台，多行业多场景文案一键生成',
          icon: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7',
          color: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
          label: null,
          visual: 'text',
          ctaTo: AGENTS_WRITING,
        },
        {
          name: 'AI营销助手',
          desc: '覆盖全链路营销场景，从内容创作到投放策略，事半功倍',
          icon: 'M22 7L13.5 15.5 8.5 10.5 2 17',
          color: 'linear-gradient(135deg,#F59E0B,#F97316)',
          label: '新功能',
          visual: 'chart',
          ctaTo: AGENTS_ASSISTANT,
        },
        {
          name: 'AI数据分析',
          desc: '上传数据文件，AI自动分析趋势并生成可视化报告',
          icon: 'M3 3v18h18M7 16l4-4 4 4 4-4',
          color: 'linear-gradient(135deg,#10B981,#14B8A6)',
          label: null,
          visual: 'chart',
          ctaTo: AGENTS_ASSISTANT,
        },
        {
          name: 'AI音乐生成',
          desc: '输入场景描述，AI生成匹配的背景音乐和音效',
          icon: 'M9 18V5l12-2v13M9 9l12-2',
          color: 'linear-gradient(135deg,#14B8A6,#06B6D4)',
          label: 'BETA',
          visual: 'music',
          ctaTo: AGENTS_APPS,
        },
        {
          name: 'AI智能客服',
          desc: '7×24小时智能客服，多轮对话理解，自动处理常见问题',
          icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
          color: 'linear-gradient(135deg,#0EA5E9,#10B981)',
          label: null,
          visual: null,
          ctaTo: AGENTS_ASSISTANT,
        },
        {
          name: 'AI智能问数',
          desc: '用自然语言提问，AI直接查询数据库返回精准数据洞察',
          icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
          color: 'linear-gradient(135deg,#F97316,#EAB308)',
          label: null,
          visual: null,
          ctaTo: AGENTS_ASSISTANT,
        },
        {
          name: 'AI报告生成',
          desc: '输入关键数据，自动生成专业的分析报告和PPT大纲',
          icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          color: 'linear-gradient(135deg,#8B5CF6,#EC4899)',
          label: 'NEW',
          visual: null,
          ctaTo: AGENTS_WRITING,
        },
      ]
    }
  },
  computed: {
    marqueeRow1() {
      const items = baseFeatures.slice(0, 8)
      return [...items, ...items]
    },
    marqueeRow2() {
      const items = baseFeatures.slice(4)
      return [...items, ...items]
    }
  }
}
</script>

<style scoped>
.features-section { background: var(--bg); }

.section-header { text-align: center; margin-bottom: 48px; }
.section-header .section-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: var(--primary-light);
  color: var(--primary);
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

/* Marquee */
.marquee-container {
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent, black 10%, black 90%, transparent);
  -webkit-mask-image: linear-gradient(90deg, transparent, black 10%, black 90%, transparent);
}
.marquee-track {
  display: flex;
  gap: 12px;
  width: max-content;
  animation: marquee 25s linear infinite;
}
.marquee-reverse .marquee-track {
  animation: marquee 30s linear infinite reverse;
}
.marquee-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
  cursor: default;
}
.marquee-item:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.marquee-icon {
  width: 28px; height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  aspect-ratio: 1;
}
.marquee-icon-svg {
  width: 16px;
  height: 16px;
  display: block;
  flex-shrink: 0;
  aspect-ratio: 1;
}

/* 卡片内图标：固定正方形画布，避免不同 path 在方形容器内视觉宽高比失调 */
.feature-icon-svg {
  width: 22px;
  height: 22px;
  display: block;
  flex-shrink: 0;
  aspect-ratio: 1;
}
.feature-icon-svg--lg {
  width: 28px;
  height: 28px;
}
.flow-node-icon {
  width: 14px;
  height: 14px;
  display: block;
  flex-shrink: 0;
  aspect-ratio: 1;
}

/* ── Row 1: Hero cards (1 row × 2 columns) ── */
.features-hero-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

/* ── Row 2-3: Small cards (2 rows × 4 columns) ── */
.features-small-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

/* ── Shared card base ── */
.feature-card {
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 22px;
  padding: 28px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
  position: relative;
}
.feature-card:hover, .feature-card.active {
  transform: translateY(-4px);
  border-color: var(--primary);
  box-shadow: var(--shadow-lg);
}

.hero-card {
  min-height: 280px;
}
.small-card {
  padding: 22px;
  min-height: 180px;
}

.feature-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.feature-icon {
  width: 48px; height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow);
  flex-shrink: 0;
  aspect-ratio: 1;
}
.large-icon { width: 56px; height: 56px; border-radius: 16px; }

.feature-label {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 12px;
  background: var(--primary-light);
  color: var(--primary);
  border-radius: 100px;
}

.feature-name {
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--text);
  line-height: 1.3;
}
.hero-name { font-size: 1.4rem; }

.feature-desc {
  font-size: 0.84rem;
  color: var(--text-muted);
  line-height: 1.7;
}
.small-desc {
  font-size: 0.78rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Hero stat badge */
.hero-stat {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 8px 16px;
  border-radius: 100px;
  width: fit-content;
}
.stat-val { font-size: 1.1rem; font-weight: 900; color: white; }
.stat-label { font-size: 0.75rem; font-weight: 600; color: rgba(255,255,255,0.85); }

/* Visuals */
.feature-visual { flex: 1; display: flex; align-items: flex-end; }
.small-visual { flex: 0; margin-top: 4px; }

.mini-chart {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 44px;
  width: 100%;
}
.chart-bar {
  flex: 1;
  background: var(--gradient-brand);
  border-radius: 3px 3px 0 0;
  opacity: 0.7;
  animation: fadeInUp 0.6s ease-out both;
  min-height: 10%;
}
.feature-card:hover .chart-bar { opacity: 1; }

/* Flow grid (for hero flow card) */
.mini-flow-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 8px 0;
}
.flow-grid-node {
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeInUp 0.5s ease-out both;
  opacity: 0.85;
}

.mini-agents {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 0;
}
.mini-agent {
  width: 44px; height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeInUp 0.5s ease-out both;
}

.mini-video {
  display: flex;
  gap: 8px;
  padding: 4px 0;
}
.video-frame {
  flex: 1;
  height: 52px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeInUp 0.4s ease-out both;
}

.music-waves {
  display: flex;
  align-items: center;
  gap: 3px;
}
.small-waves { height: 36px; }
.wave-bar {
  flex: 1;
  background: var(--gradient-brand);
  border-radius: 3px;
  opacity: 0.7;
  animation: float 0.8s ease-in-out infinite alternate;
}

.text-lines {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0;
  width: 100%;
}
.small-text { gap: 5px; }
.text-line {
  height: 7px;
  background: linear-gradient(90deg, var(--border) 0%, var(--primary-light) 50%, var(--border) 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: shimmer 2s infinite;
}

/* Tags */
.feature-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
}
.feature-tag {
  padding: 4px 10px;
  background: var(--primary-light);
  color: var(--primary);
  border-radius: 100px;
  font-size: 0.7rem;
  font-weight: 600;
}

/* Hero CTA button */
.hero-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  background: var(--gradient-brand);
  color: white;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 700;
  text-decoration: none;
  width: fit-content;
  transition: all 0.2s ease;
  box-shadow: var(--shadow);
}
.hero-cta-btn:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }

.feature-arrow {
  position: absolute;
  bottom: 18px;
  right: 18px;
  width: 30px; height: 30px;
  border-radius: 8px;
  background: var(--bg-section);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;
}
.feature-card:hover .feature-arrow {
  opacity: 1;
  transform: translateX(0);
  color: var(--primary);
}

@media (max-width: 1024px) {
  .features-small-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .features-hero-row { grid-template-columns: 1fr; }
  .features-small-grid { grid-template-columns: repeat(2, 1fr); }
  .hero-card { min-height: auto; }
}
@media (max-width: 480px) {
  .features-small-grid { grid-template-columns: 1fr; }
}
</style>
